import { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";
import {
  actionTypeSelector,
  authSelector,
  employmentCertificateSelector,
  employmentCertificatesSelector,
} from "../../reducers";
import { getLocaleText } from "../../actions/datagrid";
import {
  defaultColDef,
  gridSettings,
  sideBar,
  statusBar,
  saveViewState,
  loadViewState,
  restoreViewState,
  getRowId,
  contextMenu,
} from "../../common/grid";
import { AddEmploymentCertificate } from "./employmentcertificatemodal";

import { trashIconString, pencilIconString, restoreIconString, printerIconString } from "../../common/icons";

import { getEmploymentCertificate } from "../../actions/employmentcertificates/employmentcertificateget";
import { getAllEmploymentCertificates } from "../../actions/employmentcertificates/employmentcertificatesgetall";
import { deleteEmploymentCertificate } from "../../actions/employmentcertificates/employmentcertificatedelete";
import { postEmploymentCertificate } from "../../actions/employmentcertificates/employmentcertificatepost";
import { getPersonEmploymentCertificates } from "../../actions/employmentcertificates/employmentcertificatesgetallforperson";

import { LoadingSuspense } from "../../common/loaders/loadingsuspense";
import { PdfReport } from "../../common/pdfreport";
import { PdfModal } from "./pdfmodal";
import { fetchSettings } from "../../common/settings";
import { useQuery } from "@tanstack/react-query";
import { currentUserPersonQuery } from "../../api";

const viewStateKey = "employmentCertificateViewState";
let copy = false;

const initialState = {
  columnVisibility: {
    hiddenColIds: [],
  },
};

export default function EmploymentCertificatesView({ isPersonal }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { companyName } = useSelector(authSelector);
  const employmentCertificate = useSelector(employmentCertificateSelector);
  const employmentCertificates = useSelector(employmentCertificatesSelector);
  const actionType = useSelector(actionTypeSelector);
  const [pdf, setPdf] = useState(null);

  const [settings, setSettings] = useState({});

  const [report, setReport] = useState(null);
  const closeReport = () => setReport(null);
  const [modalShow, setModalShow] = useState(false);

  useEffect(() => {
    (async () => {
      setSettings(await fetchSettings());
      if (!isPersonal) {
        dispatch(getAllEmploymentCertificates(true));
      }
    })();

    return () =>
      dispatch({
        type: "CLEAR_EMPLOYMENT_CERTIFICATES",
        payload: { employmentCertificates: [] },
      });
  }, []);

  const person = useQuery(currentUserPersonQuery(isPersonal)).data;

  useEffect(() => {
    if (isPersonal) {
      if (person?.personId) {
        dispatch(getPersonEmploymentCertificates(person?.personId));
      }
    }
  }, [person]);

  useEffect(() => {
    if (actionType === "POST_EMPLOYMENT_CERTIFICATE_SUCCEEDED") {
      dispatch(getAllEmploymentCertificates(true));
      setModalShow(null);
    }
    if (actionType === "POST_EMPLOYMENT_CERTIFICATE_EDIT_SUCCEEDED") {
      dispatch(getAllEmploymentCertificates(true));
    }
    if (actionType === "UPLOAD_DONE") {
      setModalShow(null);
    }
    if (actionType === "GET_EMPLOYMENT_CERTIFICATE_SUCCEEDED") {
      setModalShow({ copy });
    }
    if (actionType === "DELETE_EMPLOYMENT_CERTIFICATE_SUCCEEDED") {
      /*removeGridData(gridRef, [employmentCertificate]);*/
      dispatch(getAllEmploymentCertificates(true));
    }
  }, [actionType]);

  const saveData = (data) => dispatch(postEmploymentCertificate(data));

  const removeEmploymentCertificate = (id) => {
    if (confirm(t("DeleteEmploymentCertificateConfirm", "Haluatko varmasti poistaa työtodistuksen?"))) {
      dispatch(deleteEmploymentCertificate(id));
    }
  };

  const setShow = (newState) => {
    if (isPersonal && newState) {
      return;
    }
    if (newState) setModalShow({});
    else {
      setModalShow(newState);
      copy = false;
    }
  };

  const gridRef = useRef();

  const columnDefs = [
    {
      headerName: t("Basic", "Suppea"),
      field: "basic",
      filter: "agBooleanColumnFilter",
    },
    {
      headerName: t("Identifier", "Tunniste"),
      field: "identifier",
      filter: "agNumberColumnFilter",
    },
    {
      headerName: t("PersonNumber", "Henkilönumero"),
      field: "personNumber",
      filter: "agNumberColumnFilter",
    },
    {
      headerName: t("Name", "Nimi"),
      valueGetter: (params) => params.data && `${params.data.surname} ${params.data.forename}`,
    },
    {
      headerName: t("JobTitle", "Tehtävänimike"),
      field: "profession",
    },
  ];

  const doubleClickAction = (row) => {
    if (isPersonal) {
      return;
    }
    if (!isNaN(row.node.id)) {
      dispatch(getEmploymentCertificate(row.data.id));
    }
  };

  const getContextMenuItems = useCallback((params) => {
    let items = [];

    const isPivot = params.api.isPivotMode();

    items.push({
      name: t("DefaultGridValues", "Palauta taulukon oletusasetukset"),
      action: function () {
        restoreViewState(viewStateKey, initialState);
      },
      icon: restoreIconString,
    });

    if (!isPivot && !isPersonal) {
      items.push("separator");

      items.push({
        name: t("Edit", "Muokkaa"),
        icon: pencilIconString,
        action: () => dispatch(getEmploymentCertificate(params.node.id)),
      });

      items.push({
        name: t("DeleteEmploymentCertificate", "Poista työtodistus"),
        action: () => removeEmploymentCertificate(params.node.id),
        icon: trashIconString,
      });
    }

    items.push("separator");

    if (!isPivot) {
      items.push({
        name: t("PrintEmploymentCertificate", "Tulosta työtodistus"),
        action: () => setPdf(params.node.data),
        icon: printerIconString,
      });
    }
    
    items = items.concat(
      contextMenu(t, params, setReport, {
        title: t("EmploymentCertificates", "Työtodistukset"),
        companyname: companyName,
      }),
    );

    return items;

  }, [dispatch, t, companyName, removeEmploymentCertificate, isPersonal]);

  const showReturned = () => {
    const { api } = gridRef.current;
    api.onFilterChanged();
  };

  return (
    <>
      {report !== null && <PdfReport json={report} onCloseReport={closeReport} />}
      {actionType === "GET_EMPLOYMENT_CERTIFICATE_LOADING" && <LoadingSuspense />}
      {pdf !== null && <PdfModal employmentCertificate={pdf} setPdf={setPdf} /> /* Employment certificate PDF */}
      <Row className="my-1">
        <Col sm="4">
          <h2 className="viewheading">{t("EmploymentCertificates", "Työtodistukset")}</h2>
        </Col>
        <Col sm="8" align="right">
          <Row className="g-2">
            <Col />
            <Col md="auto">
              <AddEmploymentCertificate
                employmentCertificateData={employmentCertificate}
                saveData={saveData}
                show={modalShow}
                setShow={setShow}
                setPdf={setPdf}
                person={person}
              />
            </Col>
          </Row>
        </Col>
      </Row>

      <Row className="ag-theme-material styles-ag flex-grow-1 mt-3">
        <AgGridReact
          {...gridSettings}
          ref={gridRef}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          sideBar={sideBar}
          rowData={employmentCertificates}
          statusBar={statusBar}
          onRowDoubleClicked={doubleClickAction}
          getRowId={getRowId}
          getLocaleText={(params) => getLocaleText(t, params)}
          getContextMenuItems={getContextMenuItems}
          animateRows
          suppressCellFocus
          enableCellTextSelection
          initialState={loadViewState(viewStateKey) || initialState}
          onStateUpdated={({ state }) => saveViewState(state, viewStateKey)}
          onGridReady={showReturned}
          debug={import.meta.env.DEV}
        />
      </Row>
    </>
  );
}
